import { useEffect, useRef, useState } from "react"
import GroupDropdown from "pagesV2/pro/clients/ClientWindow/GroupManager"
import { Modal } from "ui/Modal"
import { useTranslation } from "react-i18next"
import { Button, Flex } from "antd"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"

const getSortedGroupIds = (groups = []) =>
  groups
    ?.map((group) => group?.group_id ?? group?.id)
    ?.filter(Boolean)
    ?.map(String)
    ?.sort((a, b) => a.localeCompare(b)) || []

const areGroupCollectionsEqual = (firstGroups = [], secondGroups = []) => {
  const firstIds = getSortedGroupIds(firstGroups)
  const secondIds = getSortedGroupIds(secondGroups)

  if (firstIds.length !== secondIds.length) {
    return false
  }

  return firstIds.every((groupId, index) => groupId === secondIds[index])
}

const ClientGroupManage = ({
  groupAction,
  isGroupModalVisible,
  setIsGroupModalVisible,
  selectedClient,
  selectedClientIds,
  groups,
  handleSelectedGroupsChange,
  handleGroupActionClick,
  isMenuListHide,
  setSelectedClientIds,
  selectedGroups,
  handleCancelGroupAction,
  removeGroups
}) => {
  const { t } = useTranslation()
  const { loading } = useSelector((state) => state.clients.groupClients)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const initialSelectedGroupsRef = useRef([])
  const initialRemoveGroupsRef = useRef([])
  const isClientAdd = groupAction === "bulk_add" || groupAction === "add"
  const isSaveBtnDisabled =
    ((groupAction === "bulk_remove" || groupAction === "remove") && removeGroups.length === 0) ||
    (selectedGroups.length === 0 && removeGroups.length === 0)

  useEffect(() => {
    if (!isGroupModalVisible) {
      return
    }

    initialSelectedGroupsRef.current = [...selectedGroups]
    initialRemoveGroupsRef.current = [...removeGroups]
    setShowUnsavedModal(false)
  }, [isGroupModalVisible])

  const hasUnsavedChanges =
    !areGroupCollectionsEqual(selectedGroups, initialSelectedGroupsRef.current) ||
    !areGroupCollectionsEqual(removeGroups, initialRemoveGroupsRef.current)

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true)
      return
    }

    handleCancelGroupAction()
  }

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false)
    handleCancelGroupAction()
  }

  if (loading) return null

  return (
    <>
      <Modal
        width={{ xxl: 800, xl: 700 }}
        title={
          isClientAdd ? t("clients.bulk_actions.add_selected_client_to_group") : t("clients.bulk_actions.remove_selected_client_from_group")
        }
        open={isGroupModalVisible}
        footer={null}
        onCancel={handleRequestClose}
        destroyOnClose
      >
        <GroupDropdown
          action={groupAction}
          menuState={true}
          isMenuListHide={isMenuListHide}
          client={selectedClient}
          selectedClientIds={selectedClient ? undefined : selectedClientIds}
          groups={groups}
          setIsGroupModalVisible={setIsGroupModalVisible}
          selectedGroups={selectedClient?.client_groups || selectedGroups || []}
          onSelectedGroupsChange={handleSelectedGroupsChange}
          moduleName={"client"}
          setSelectedClientIds={setSelectedClientIds}
          groupAction={groupAction}
        />
        <Flex gap={12} className="my-xl">
          <Button variant="outlined" color="primary" onClick={handleRequestClose}>
            {t("global.cancel")}
          </Button>
          <Button type="primary" onClick={handleGroupActionClick} disabled={isSaveBtnDisabled}>
            {t("global.save")}
          </Button>
        </Flex>
      </Modal>

      <UnsavedChangesModal open={showUnsavedModal} onDiscard={handleDiscardChanges} onKeepEditing={() => setShowUnsavedModal(false)} />
    </>
  )
}

ClientGroupManage.propTypes = {
  groupAction: PropTypes.string,
  isGroupModalVisible: PropTypes.bool,
  setIsGroupModalVisible: PropTypes.func,
  selectedClient: PropTypes.object,
  selectedClientIds: PropTypes.array,
  groups: PropTypes.array,
  handleSelectedGroupsChange: PropTypes.func,
  handleGroupActionClick: PropTypes.func,
  isMenuListHide: PropTypes.bool,
  setSelectedClientIds: PropTypes.func,
  selectedGroups: PropTypes.array,
  handleCancelGroupAction: PropTypes.func,
  removeGroups: PropTypes.array
}

export default ClientGroupManage
